import KudosLogo from '@assets/images/KudosLogo.svg'
import { useAuth } from '@auth/useAuth'
import MediaImage from '@components/MediaImage'
import TrialBanner from '@components/TrialBanner'
import AppPaths from '@globals/paths'
import { tabletQuery } from '@globals/themes/themeConstants'
import { useTenantMetadata } from '@globals/useTenantMetadata'
import { useTrialBannerHeight } from '@globals/useTrialBannerStore'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import InAppNotifications from '@shared/inAppNotifications/InAppNotifications'
import useUpgradeLicense from '@shared/licensing/useUpgradeLicense'
import TenantMemberAvatar from '@shared/tenantMemberAvatar/TenantMemberAvatar'
import { T, useT } from '@transifex/react'
import { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router'
import MobileProgressiveWebAppInstallPrompt from '../routes/protected/tenant/mobile/menus/PWAInstallationPrompt'
import { NavContainer, StyledArrowBackIcon, StyledListItem } from './Nav.styled'
import { NavContents } from './NavContents'
import { NavDrawerDesktop } from './NavDrawerDesktop'
import { NavDrawerMobile } from './NavDrawerMobile'
import { LinkItem } from './types'
import { useNav } from './useNav'

export type RenderContext = {
  onCollapse: (key: string) => void
  expanded: string[]
  onLinkClicked: (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    linkItem: LinkItem,
  ) => void
}

const listStyle: SxProps = {
  paddingTop: '0px !important',
}

const mobileNavProps: SxProps = {
  '> ul': {
    padding: 0,
  },
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  justifyContent: 'end',
}

const desktopNavProps: SxProps = {
  flexGrow: 1,
}

export const Nav: React.FC = () => {
  const t = useT()
  const { isAuthenticated, isTenantScope } = useAuth()
  const { interceptClick } = useUpgradeLicense()
  const { visibleItems, mode, setMode, expanded, onCollapse, canViewAdmin } = useNav(
    isAuthenticated,
    isTenantScope,
  )
  const [metadata] = useTenantMetadata()
  const { setTrialBannerHeight } = useTrialBannerHeight()
  const isTablet = useMediaQuery(tabletQuery)
  const [isMobileDrawerOpen, setisMobileDrawerOpen] = useState<boolean>(false)
  const navigate = useNavigate()

  const toggleDrawer = () => {
    setisMobileDrawerOpen(!isMobileDrawerOpen)
  }

  // Nav is not supported outside of authenticated context
  if (!isAuthenticated || mode === 'hidden') return null

  const renderContext: RenderContext = {
    onCollapse,
    expanded,
    onLinkClicked: (event, linkItem) => {
      if (linkItem.showUpgradeDialog) interceptClick(event, linkItem.showUpgradeDialog)
      toggleDrawer()
    },
  }

  const navContainerContent: ReactNode = (
    <>
      <NavContainer>
        {isAuthenticated && mode === 'default' && isTablet && (
          <MobileProgressiveWebAppInstallPrompt />
        )}

        <Box component="nav" sx={!isTablet ? desktopNavProps : mobileNavProps}>
          <NavContents navItems={visibleItems} context={renderContext} isMobile={isTablet} />
        </Box>

        {isAuthenticated && mode === 'default' && (
          <nav>
            <List sx={listStyle}>
              <Divider />
              {canViewAdmin && (
                <StyledListItem>
                  <ListItemButton onClick={() => setMode('admin')}>
                    <ListItemIcon>
                      <BadgeOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary={<T _str="Switch to admin menu" />} />
                  </ListItemButton>
                </StyledListItem>
              )}
            </List>
          </nav>
        )}
        {isAuthenticated && mode === 'admin' && (
          <>
            <Divider />
            <nav>
              <List sx={listStyle}>
                <StyledListItem>
                  <ListItemButton
                    onClick={() => {
                      setMode('default')
                      if (isTablet) navigate(AppPaths.tenant.menu())
                      if (isMobileDrawerOpen) {
                        toggleDrawer()
                      }
                    }}>
                    <ListItemIcon>
                      <StyledArrowBackIcon />
                    </ListItemIcon>
                    <ListItemText primary={<T _str="Back to previous menu" />} />
                  </ListItemButton>
                </StyledListItem>
              </List>
            </nav>
          </>
        )}
      </NavContainer>

      {!isTablet && (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0.5,
          }}>
          <Typography variant="caption">
            <T _str="Powered by" />
          </Typography>
          <Box
            sx={{
              height: 20,
              width: 90,
              opacity: 0.3,
            }}>
            <MediaImage imgSrc={KudosLogo} backgroundColor="transparent" alt={t('Kudos Logo')} />
          </Box>
        </Stack>
      )}
    </>
  )

  return (
    <>
      {!isTablet && (
        <>
          <TrialBanner metadata={metadata} onHeightChange={setTrialBannerHeight} />
          <InAppNotifications />
        </>
      )}
      {isTablet ? (
        <NavDrawerMobile
          isMobileDrawerOpen={isMobileDrawerOpen}
          onMobileDrawerToggle={toggleDrawer}>
          {navContainerContent}
        </NavDrawerMobile>
      ) : (
        <>
          <TenantMemberAvatar />
          <InAppNotifications />
          <NavDrawerDesktop>{navContainerContent}</NavDrawerDesktop>
        </>
      )}
    </>
  )
}
